import { useSelector } from 'react-redux';
import { Field, NumericInput, TextOutput, RadioGroup } from '../../../../Core';
import { PageSection, ListFieldSection } from '../../../../Layout';
import { WorksheetAssociatedTestSection } from '../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import { hasValue } from 'Lib/Any';
import externalTestMethods from '../../../ExternalTestMethods';

let OversizeCorrectionSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const visible = useSelector(createWorksheetSelector!('data.Preparation.OversizeCorrectionRequired', false));
  const importedSpecificGravityCoarseTestId = useSelector(
    createWorksheetSelector!('AssociatedSpecificGravityCoarse.ImportedTest.Id')
  ) as Guid;

  const hideMaterialTakenAs = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.AssociatedOversizeDetermination.ExternalTest', false), hasValue)
  );

  const specificGravityIsAssumed = useSelector(createWorksheetSelector!('SpecificGravityCoarseAssumed'));
  const specificGravityOvenDry = useSelector(
    createWorksheetSelector!('SpecificGravityCoarseResults.SpecificGravityOvenDry')
  );

  return !visible ? null : (
    <PageSection heading="Oversize Correction Data">
      <ListFieldSection single>
        <Field
          input={<RadioGroup />}
          label="Specific Gravity of Coarse Material Determined By"
          name="SpecificGravityCoarseAssumed"
          confirm={() => (specificGravityIsAssumed ? !!specificGravityOvenDry : true)}
          confirmTitle="Confirm Determination Change"
          confirmText="Any existing data for this results determination will be removed."
          confirmButtonText="Change Determination"
          options={[
            { label: 'Assumed', value: true },
            { label: 'Measured', value: false },
          ]}
        />
        <Field
          input={<NumericInput />}
          label="Specific Gravity (Oven Dry)"
          name="AssumedSpecificGravity"
          visible={specificGravityIsAssumed}
        />
        <Field
          input={<NumericInput />}
          label="Absorption (%)"
          name="AssumedAbsorption"
          visible={specificGravityIsAssumed}
        />
      </ListFieldSection>
      {!specificGravityIsAssumed && (
        <WorksheetAssociatedTestSection
          heading="Specific Gravity of Coarse Material"
          label="Determined Method:"
          name="AssociatedSpecificGravityCoarse"
          testType="Specific Gravity Coarse"
          associatedTestModel={{
            measured: {
              measuredTestSetup: (
                <ListFieldSection visible={!hideMaterialTakenAs} wide>
                  <Field
                    input={<RadioGroup />}
                    label="Material Taken as"
                    name="MeasuredSpecificGravityCoarseMaterialSource"
                    options={[
                      { label: 'Full sample retained on oversize sieve', value: 'oversize_full_sample' },
                      { label: 'Sub sample of material retained on oversize sieve', value: 'oversize_sub_sample' },
                    ]}
                  />
                </ListFieldSection>
              ),
            },
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field
                    input={<TextOutput />}
                    label="Specific Gravity (Oven Dry)"
                    name="SpecificGravityCoarseResults.SpecificGravityOvenDry"
                    emph
                  />
                  <Field
                    input={<TextOutput />}
                    label="Specific Gravity (SSD)"
                    name="SpecificGravityCoarseResults.SpecificGravitySSD"
                    emph
                  />
                  <Field
                    input={<TextOutput />}
                    label="Apparent Specific Gravity"
                    name="SpecificGravityCoarseResults.ApparentSpecificGravity"
                    emph
                  />
                  <Field
                    input={<TextOutput />}
                    label="Absorption (%)"
                    name="SpecificGravityCoarseResults.Absorption"
                    emph
                  />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedSpecificGravityCoarseTestId,
              },
            },
            external: {
              testMethods: [externalTestMethods.specificGravity.AASHTO_T_85],
              externalTestResults: (
                <ListFieldSection single>
                  <Field
                    input={<NumericInput />}
                    label="Specific Gravity (Oven Dry)"
                    name="AssociatedSpecificGravityCoarse.ExternalTest.Results.SpecificGravityOvenDry"
                    emph
                  />
                  <Field
                    input={<NumericInput />}
                    label="Absorption (%)"
                    name="AssociatedSpecificGravityCoarse.ExternalTest.Results.Absorption"
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      )}
    </PageSection>
  );
};

OversizeCorrectionSection = withInjectedProps(OversizeCorrectionSection);

export { OversizeCorrectionSection };
export default OversizeCorrectionSection;
